export const revivalApps = [
  "api",
  "desktop",
  "web",
  "mobile",
  "admin",
] as const;
export type RevivalApp = (typeof revivalApps)[number];

export const revivalPlans = ["STARTER", "STANDARD", "PROFESSIONAL"] as const;
export type RevivalPlan = (typeof revivalPlans)[number];

export type CapabilityCode =
  | "CORE_PHARMACY"
  | "COMPLIANCE"
  | "MULTI_STORE"
  | "GROWTH";

export interface CapabilityDefinition {
  code: CapabilityCode;
  releaseEnabled: boolean;
  dependencies: readonly CapabilityCode[];
  apps: readonly RevivalApp[];
  plans: readonly RevivalPlan[];
  routes: readonly string[];
  permissions: readonly string[];
}

const allPlans = revivalPlans;

export const productRevivalCapabilities = {
  CORE_PHARMACY: {
    code: "CORE_PHARMACY",
    releaseEnabled: true,
    dependencies: [],
    apps: ["api", "desktop", "web", "admin"],
    plans: allPlans,
    routes: [
      "/pos",
      "/invoices",
      "/sales-returns",
      "/approvals",
      "/goods-receipts",
      "/products",
      "/batches",
      "/stock-adjustments",
      "/customers",
      "/suppliers",
      "/imports",
      "/readiness",
      "/stores",
      "/counters",
      "/printer-config",
    ],
    permissions: [
      "sales.read",
      "sales.create",
      "sales.return",
      "inventory.read",
      "inventory.count",
      "procurement.receive",
      "catalog.read",
      "catalog.import",
      "parties.read",
      "reports.basic",
      "settings.store",
    ],
  },
  COMPLIANCE: {
    code: "COMPLIANCE",
    releaseEnabled: true,
    dependencies: ["CORE_PHARMACY"],
    apps: ["api", "desktop", "web", "mobile", "admin"],
    plans: ["STANDARD", "PROFESSIONAL"],
    routes: [
      "/prescriptions",
      "/schedule-h1",
      "/drug-licenses",
      "/gst-compliance",
    ],
    permissions: ["compliance.read", "compliance.capture"],
  },
  MULTI_STORE: {
    code: "MULTI_STORE",
    releaseEnabled: true,
    dependencies: ["CORE_PHARMACY"],
    apps: ["api", "desktop", "web", "mobile", "admin"],
    plans: ["PROFESSIONAL"],
    routes: ["/active-store", "/stock-transfers"],
    permissions: ["stores.switch", "stock.transfer"],
  },
  GROWTH: {
    code: "GROWTH",
    releaseEnabled: false,
    dependencies: ["CORE_PHARMACY"],
    apps: ["api", "desktop", "web", "admin"],
    plans: ["PROFESSIONAL"],
    routes: [
      "/loyalty-programs",
      "/discount-schemes",
      "/analytics",
      "/integrations",
    ],
    permissions: ["growth.manage"],
  },
} as const satisfies Record<CapabilityCode, CapabilityDefinition>;

export interface CapabilityValidationIssue {
  capability: CapabilityCode;
  field: "code" | "dependencies" | "apps" | "plans" | "routes" | "permissions";
  message: string;
}

export function validateProductRevivalCapabilities(): CapabilityValidationIssue[] {
  const issues: CapabilityValidationIssue[] = [];
  const routeOwners = new Map<string, CapabilityCode>();
  const definitions = Object.entries(productRevivalCapabilities) as [
    CapabilityCode,
    CapabilityDefinition,
  ][];

  for (const [key, definition] of definitions) {
    if (key !== definition.code) {
      issues.push({
        capability: key,
        field: "code",
        message: "registry key and code differ",
      });
    }

    for (const dependency of definition.dependencies) {
      if (!(dependency in productRevivalCapabilities)) {
        issues.push({
          capability: key,
          field: "dependencies",
          message: `unknown dependency ${dependency}`,
        });
      }
    }

    for (const app of definition.apps) {
      if (!revivalApps.includes(app)) {
        issues.push({
          capability: key,
          field: "apps",
          message: `unknown app ${app}`,
        });
      }
    }

    for (const plan of definition.plans) {
      if (!revivalPlans.includes(plan)) {
        issues.push({
          capability: key,
          field: "plans",
          message: `unknown plan ${plan}`,
        });
      }
    }

    for (const route of definition.routes) {
      const normalized = route.replace(/\/+$/, "") || "/";
      const owner = routeOwners.get(normalized);
      if (owner && owner !== key) {
        issues.push({
          capability: key,
          field: "routes",
          message: `route ${normalized} is already owned by ${owner}`,
        });
      } else {
        routeOwners.set(normalized, key);
      }
    }

    if (
      new Set(definition.permissions).size !== definition.permissions.length
    ) {
      issues.push({
        capability: key,
        field: "permissions",
        message: "duplicate permission",
      });
    }
  }

  const visit = (
    code: CapabilityCode,
    active: Set<CapabilityCode>,
    visited: Set<CapabilityCode>,
  ) => {
    if (active.has(code)) {
      issues.push({
        capability: code,
        field: "dependencies",
        message: "dependency cycle",
      });
      return;
    }
    if (visited.has(code)) return;
    active.add(code);
    for (const dependency of productRevivalCapabilities[code].dependencies) {
      visit(dependency, active, visited);
    }
    active.delete(code);
    visited.add(code);
  };

  const visited = new Set<CapabilityCode>();
  for (const code of Object.keys(
    productRevivalCapabilities,
  ) as CapabilityCode[]) {
    visit(code, new Set(), visited);
  }

  return issues;
}

export function isRevivalCapabilityEnabled(
  code: CapabilityCode,
  enabled: ReadonlySet<CapabilityCode>,
): boolean {
  const definition = productRevivalCapabilities[code];
  return (
    definition.releaseEnabled &&
    enabled.has(code) &&
    definition.dependencies.every((dependency) =>
      isRevivalCapabilityEnabled(dependency, enabled),
    )
  );
}
